import { Subject, combineLatest } from 'rxjs';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import { Injectable } from '@angular/core';
import { FormControl, Validators, FormGroup, AbstractControl, ValidatorFn, FormArray, ValidationErrors } from '@angular/forms';

import { FleetCardsFeatureFlagService } from '@services/feature-flags/fleet-cards';
import { Session } from '@services/session';
import { FleetCardsDataService } from '@services/fleet-cards-data';

import { IPersonalizationItem, IServerCardOrder, IServerPersonalizationItem } from './interfaces';
import { IServerShippingAddress } from '../../orders.interface';
import { IDoc } from '@app/admin/fleet-card/shared/document-upload-modal/document-upload-modal.component';
import { ISelectedValue } from '@fleet-cards/shared/vehicle-driver-search-input.component';

import { SHIPPING_TYPES, EMOJI_PATTERN } from '@app/admin/fleet-card/card-application/card-application.constants';
import { ENTITY_TYPE, MESSAGES } from '@app/dashboard/fleet-cards/main.constants';
import {
  PersonalizationItemKeys,
  ShippingAddressFormKeys,
  CheckoutFormGroupKeys,
  InitialRequirementsFormKeys,
  PersonalizationFormKeys,
  DEFAULT_COUNTRY,
  VALIDATE_ASSIGNEE_ERROR_MAP,
  VALIDATE_ASSIGNEE_ERROR_KEYS,
  BULK_ENTITY_SELECTION_CONFIG,
  HAD_ERRORS_KEY,
  CARD_TYPE_OPTIONS,
  CARD_ID_MAX_LENGTH,
  CARD_ID_MAX_LENGTH_FOR_VEHICLES,
} from './constants';
import { CARD_ENTITIES } from '@app/admin/fleet-card/main.constants';
import { IFleetCardCompany } from '@app/admin/fleet-card/shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';

export function getErrorMessage(control: FormControl | AbstractControl, name: string): string {
  const controlErrors: ValidationErrors = control.errors;
  const isEmpty: boolean = controlErrors && controlErrors['required'];
  const invalid: boolean = controlErrors && controlErrors['invalid'];
  const maxLength: boolean = controlErrors && controlErrors['maxlength'];
  const minLength: boolean = controlErrors && controlErrors['minlength'];
  const minValue: boolean = controlErrors && controlErrors['min'];
  const maxValue: boolean = controlErrors && controlErrors['max'];
  const pattern: boolean = controlErrors && controlErrors['pattern'];
  if (isEmpty) {
    return MESSAGES.INFO_IS_REQUIRED;
  } else if (minValue) {
    return controlErrors['min'].min === 1 ? MESSAGES.VALUE_GREATER_THAN_ZERO : MESSAGES.INFO_IS_REQUIRED;
  } else if (invalid || maxLength || minLength || pattern) {
    return `${name} ${MESSAGES.INVALID}`;
  } else if (maxValue) {
    return $localize`You can order up to ${control.errors.max.max} cards based on your fleet size.`;
  }
}

const ValidatePersonalizationItem: ValidatorFn = (group: FormGroup) => {
  if (
    group.get(PersonalizationItemKeys.cardType).valid &&
    (group.get(PersonalizationItemKeys.userId).value || group.get(PersonalizationItemKeys.vehicleId).value) &&
    !group.get(PersonalizationItemKeys.nameOnCard).valid
  ) {
    group[HAD_ERRORS_KEY] = true;
    return { nameOnCard: true };
  }

  if (
    group.get(PersonalizationItemKeys.cardType).valid &&
    group.get(PersonalizationItemKeys.nameOnCard).valid &&
    group.get(PersonalizationItemKeys.isValidated).value
  ) {
    if (group.get(PersonalizationItemKeys.userId).value || group.get(PersonalizationItemKeys.vehicleId).value) {
      return null;
    }
  }

  if (!group.get(PersonalizationItemKeys.isValidated).value && group.errors?.validationError) {
    group[HAD_ERRORS_KEY] = true;
    return group.errors;
  }
};

const ValidatePersonalizationForm: ValidatorFn = (group: FormArray) => {
  let isGroupValid: boolean = true;
  group.controls.forEach((control: FormGroup) => {
    if (!control.get(PersonalizationItemKeys.isValidated).value) {
      isGroupValid = false;
      return;
    }
  });
  if (isGroupValid) return null;
  return { notValidated: true };

};

export const newAddressFormControls = {
  [ShippingAddressFormKeys.address1]: new FormControl('', Validators.compose(
    [
      Validators.required,
      Validators.pattern(EMOJI_PATTERN),
    ],
  )),
  [ShippingAddressFormKeys.address2]: new FormControl(''),
  [ShippingAddressFormKeys.postal_code]: new FormControl('', Validators.compose(
    [
      Validators.minLength(5),
      Validators.maxLength(10),
      Validators.required,
      Validators.pattern('^[0-9]{5}(-?[0-9]{4}){0,1}$'),
    ],
  )),
  [ShippingAddressFormKeys.state]: new FormControl('', Validators.required),
  [ShippingAddressFormKeys.city]: new FormControl('', Validators.compose(
    [
      Validators.required,
      Validators.pattern(EMOJI_PATTERN),
    ],
  )),
  [ShippingAddressFormKeys.proofs]: new FormControl('', Validators.required),
};

const form = {
  [CheckoutFormGroupKeys.initialRequirementsForm]: new FormGroup({
    [InitialRequirementsFormKeys.cardType]: new FormControl(CARD_TYPE_OPTIONS[0].value),
    [InitialRequirementsFormKeys.quantity]: new FormControl('', Validators.compose(
      [
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.min(1),
      ]),
    ),
    [InitialRequirementsFormKeys.reason]: new FormControl(''),
    [InitialRequirementsFormKeys.personalization]: new FormControl(''),
  }),
  [CheckoutFormGroupKeys.shippingAddressForm]: new FormGroup({
    [ShippingAddressFormKeys.id]: new FormControl('', Validators.required),
    [ShippingAddressFormKeys.recipientName]: new FormControl('', Validators.compose(
      [
        Validators.required,
        Validators.pattern(/^[a-z][a-z ]*$/i),
        Validators.minLength(2),
      ]),
    ),
    [ShippingAddressFormKeys.recipientPhoneNumber]: new FormControl('', Validators.compose(
      [
        Validators.required,
        Validators.minLength(10),
        Validators.pattern('^[^_]*$'),
      ]),
    ),
    [ShippingAddressFormKeys.expediteShipping]: new FormControl(SHIPPING_TYPES.STANDARD, Validators.required),
  }),
};

@Injectable({
  providedIn: 'root',
})
export class CheckoutForm extends FormGroup {
  // Usage Configuration
  public importedCSV: boolean = false;
  public usedBulkEntitySelect: boolean = false;

  public hasCreatedOrder: boolean = false;
  public filesToUpload: IDoc[] = [];

  private ROWS_THRESHOLD: number = 100;

  public readonly bulkEntitySelectionConfig: typeof BULK_ENTITY_SELECTION_CONFIG = BULK_ENTITY_SELECTION_CONFIG;
  public isFleetCardsCardOrderImprovementsEnabled: boolean;
  public isFleetCardsFleetUserCardsEnabled: boolean = false;
  public isFirstCardOrder: Subject<boolean> = new Subject<boolean>();
  public firstOrderDetails: any = {}; // TODO: update name
  public draftOrderId: string;
  public isViaCardPostAppOnboardingFlow: boolean = false; // todo
  public isViaPersonalizationFlow: boolean = false; // todo
  
  constructor(
    private fleetCardFeatureFlagService: FleetCardsFeatureFlagService,
    private fleetCardsDataService: FleetCardsDataService,
    private session: Session,
  ) {
    super(form);
    this.isFleetCardsFleetUserCardsEnabled = this.fleetCardFeatureFlagService.isFleetCardsFleetUserCardsEnabled();

    this.initializeCardOrderFeatureFlags();
  }

  public isFieldRequired(formGroupKey: string, formControlKey: string): boolean {
    const control: AbstractControl = this.get(formGroupKey)['controls'][formControlKey] as AbstractControl;
    if (!control.validator) {
      return false;
    }

    const validator = control.validator({} as AbstractControl);
    return (validator && validator.required);
  }

  public addCardTypeValidation(): void {
    this.get(
      CheckoutFormGroupKeys.initialRequirementsForm,
    )['controls'][InitialRequirementsFormKeys.cardType].setValidators([Validators.required]);
  }

  public addOrderReasonValidation(): void {
    this.get(
      CheckoutFormGroupKeys.initialRequirementsForm,
    )['controls'][InitialRequirementsFormKeys.reason].setValidators([Validators.required]);
  }

  public removeOrderReasonValidation(): void {
    this.get(
      CheckoutFormGroupKeys.initialRequirementsForm,
    )['controls'][InitialRequirementsFormKeys.reason].setValidators([]);
  }

  public addPersonalizationValidation(): void {
    this.get(
      CheckoutFormGroupKeys.initialRequirementsForm,
    )['controls'][InitialRequirementsFormKeys.personalization].setValidators([Validators.required]);
  }

  public addNewAddressForm(): void {
    Object.keys(newAddressFormControls).forEach((key: string) => {
      (this.get(CheckoutFormGroupKeys.shippingAddressForm) as FormGroup).addControl(key, newAddressFormControls[key]);
    });
    (this.get(CheckoutFormGroupKeys.shippingAddressForm) as FormGroup).get(ShippingAddressFormKeys.id).clearValidators();
    (this.get(CheckoutFormGroupKeys.shippingAddressForm) as FormGroup)
      .get(ShippingAddressFormKeys.id).updateValueAndValidity();
  }

  public removeNewAddressForm(keepProofsControl: boolean = false): void {
    const addressFormControls = {...newAddressFormControls};
    if (keepProofsControl) {
      // proofs is deleted from addressFormControls which means it will not be removed from form group
      delete addressFormControls[ShippingAddressFormKeys.proofs];
    }
    Object.keys(addressFormControls).forEach((key: string) => {
      (this.get(CheckoutFormGroupKeys.shippingAddressForm) as FormGroup).removeControl(key);
    });
    (this.get(CheckoutFormGroupKeys.shippingAddressForm) as FormGroup)
      .get(ShippingAddressFormKeys.id).setValidators(Validators.required);

    (this.get(CheckoutFormGroupKeys.shippingAddressForm) as FormGroup)
      .get(ShippingAddressFormKeys.id).updateValueAndValidity();
  }

  public addPersonalizationForm(): void {
    if (this.importedCSV) return;

    if (!this.contains(CheckoutFormGroupKeys.personalizationForm)) {
      this.addControl(CheckoutFormGroupKeys.personalizationForm, new FormGroup({
        [PersonalizationFormKeys.personalizationItems]: new FormArray([]),
      }));
      (this.get(CheckoutFormGroupKeys.personalizationForm) as FormGroup).addControl(
        PersonalizationFormKeys.documentId,
        new FormControl(''),
      );

      (this.get(CheckoutFormGroupKeys.personalizationForm) as FormGroup).get(
        PersonalizationFormKeys.personalizationItems,
      ).setValidators(ValidatePersonalizationForm);
    }

    // We do not care about quantity if bulk entity select used
    if (this.usedBulkEntitySelect) return;

    const personalizationItems: FormArray = ((this.get(CheckoutFormGroupKeys.personalizationForm) as FormGroup)
      .get(PersonalizationFormKeys.personalizationItems) as FormArray);

    const numberOfCards: number = Number(this.get(CheckoutFormGroupKeys.initialRequirementsForm)
    ['controls'][InitialRequirementsFormKeys.quantity].value);

    if (numberOfCards < personalizationItems.controls.length) {
      personalizationItems.controls = personalizationItems.controls.splice(0, numberOfCards);
    } else {
      while (personalizationItems.controls.length < Math.min(numberOfCards, this.ROWS_THRESHOLD)) {
        personalizationItems.push(this.createPersonalizationControl());
      }
    }
  }

  public resetPersonalizationItems(): void {
    const personalizationForm = this.get(CheckoutFormGroupKeys.personalizationForm) as FormGroup;

    if (personalizationForm) {
      const personalizationItems = personalizationForm.get(PersonalizationFormKeys.personalizationItems) as FormArray;
      personalizationItems.clear();
    }
  }

  public incrementQuantity(): void {
    this.get(
      CheckoutFormGroupKeys.initialRequirementsForm,
    ).get(InitialRequirementsFormKeys.quantity).setValue(
      Number(this.get(
        CheckoutFormGroupKeys.initialRequirementsForm,
      ).get(InitialRequirementsFormKeys.quantity).value) + 1,
    );
  }

  public decrementQuantity(): void {
    this.get(
      CheckoutFormGroupKeys.initialRequirementsForm,
    ).get(InitialRequirementsFormKeys.quantity).setValue(
      Number(this.get(
        CheckoutFormGroupKeys.initialRequirementsForm,
      ).get(InitialRequirementsFormKeys.quantity).value) - 1,
    );
  }

  public appendPersonalizationControl(): void {
    const personaliationItems: FormArray = ((this.get(CheckoutFormGroupKeys.personalizationForm) as FormGroup)
      .get(PersonalizationFormKeys.personalizationItems) as FormArray);

    personaliationItems.push(this.createPersonalizationControl());
  }

  public appendPersonalizationWithControl(item: FormGroup): void {
    const personaliationItems: FormArray = ((this.get(CheckoutFormGroupKeys.personalizationForm) as FormGroup)
      .get(PersonalizationFormKeys.personalizationItems) as FormArray);

    personaliationItems.push(item);
  }


  public createPersonalizationControl(): FormGroup {
    const personalizationItem: FormGroup = new FormGroup({
      [PersonalizationItemKeys.name]: new FormControl(''),
      [PersonalizationItemKeys.cardType]: new FormControl('', Validators.required),
      [PersonalizationItemKeys.nameOnCard]: new FormControl('', Validators.compose(
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(CARD_ID_MAX_LENGTH),
        ]),
      ),
      [PersonalizationItemKeys.vehicleId]: new FormControl(''),
      [PersonalizationItemKeys.userId]: new FormControl(''),
      [PersonalizationItemKeys.entity]: new FormControl('', Validators.required),
      [PersonalizationItemKeys.isValidated]: new FormControl(false),
      [PersonalizationItemKeys.lastValidatedValue]: new FormControl(''),
      [PersonalizationItemKeys.cardIdSuggestions]: new FormControl([]),
    });

    personalizationItem.setValidators(ValidatePersonalizationItem);
    if (this.isFleetCardsCardOrderImprovementsEnabled) {
      personalizationItem.controls[PersonalizationItemKeys.nameOnCard].setValidators(
        Validators.compose([
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(CARD_ID_MAX_LENGTH),
          Validators.pattern(/^[A-Za-z0-9\-., ]+$/),
        ]),
      );
    }

    combineLatest(
      [
        personalizationItem.controls[PersonalizationItemKeys.entity].valueChanges,
        personalizationItem.controls[PersonalizationItemKeys.nameOnCard].valueChanges,
      ],
    ).subscribe(([entityVal, nameOnCardVal]) => {
      if (!entityVal && !nameOnCardVal) {
        personalizationItem.setErrors(null);
      }
      personalizationItem.controls[PersonalizationItemKeys.isValidated].setValue(false);
      personalizationItem.controls[PersonalizationItemKeys.lastValidatedValue].setValue({});
      personalizationItem.updateValueAndValidity();
    });

    return personalizationItem;
  }

  public importPersonalizationItems(items: IPersonalizationItem[], documentId?: string): void {
    if (documentId) {
      (this.get(CheckoutFormGroupKeys.personalizationForm) as FormGroup)
      .get(PersonalizationFormKeys.documentId).setValue(documentId);
    }

    const personalizationItems: FormArray = ((this.get(CheckoutFormGroupKeys.personalizationForm) as FormGroup)
      .get(PersonalizationFormKeys.personalizationItems) as FormArray);

    const count = items.length;
    if (count < personalizationItems.controls.length) {
      personalizationItems.controls = personalizationItems.controls.splice(0, count);
    } else {
      while (personalizationItems.controls.length < count) {
        personalizationItems.push(this.createPersonalizationControl());
      }
    }

    if (this.isFleetCardsCardOrderImprovementsEnabled) {
      personalizationItems.controls.forEach((control: FormGroup) => {
        control.controls[PersonalizationItemKeys.nameOnCard].clearValidators();
      });
    }

    personalizationItems.reset();
    items.forEach((item: IPersonalizationItem, index) => {
      (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.cardType).setValue(item.cardType);
      if (item.cardType === CARD_ENTITIES.DRIVER || item.cardType === CARD_ENTITIES.FLEET_USER) {
        (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.userId).setValue(item.userId);
      } else {
        (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.vehicleId).setValue(item.vehicleId);
      }
      (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.nameOnCard).setValue(item.nameOnCard);
      (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.name).setValue(item.name);
      (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.entity).clearValidators();
      (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.entity).updateValueAndValidity();
      (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.isValidated).setValue(true);
      if (this.usedBulkEntitySelect) {
        (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.entity).setValue({
          entityType: item.cardType,
          entity: {
            id: (item.userId || item.vehicleId) as string | number,
            name: item.name,
            description: item.name,
            score: 0,
            active: true,
          },
        } as ISelectedValue);
        (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.nameOnCard).updateValueAndValidity();
        (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.nameOnCard).markAsDirty();
        (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.entity).markAsDirty();
        (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.cardIdSuggestions).setValue([]);
        (personalizationItems.at(index) as FormGroup).get(PersonalizationItemKeys.isValidated).setValue(false);
        (personalizationItems.at(index) as FormGroup)[HAD_ERRORS_KEY] = false;
      }
    });
    personalizationItems.setErrors(null);
  }

  public appendPersonalizationItems(items: IPersonalizationItem[]): FormGroup[] {
    const personalizationItems: FormArray = ((this.get(CheckoutFormGroupKeys.personalizationForm) as FormGroup)
      .get(PersonalizationFormKeys.personalizationItems) as FormArray);

    const initialLength: number = personalizationItems.controls.length;

    items.forEach(() => {
      personalizationItems.push(this.createPersonalizationControl());
    });

    items.forEach((item: IPersonalizationItem, index) => {
      const formIndex: number = initialLength + index;
      const formGroup: FormGroup = personalizationItems.at(formIndex) as FormGroup;

      formGroup.get(PersonalizationItemKeys.cardType).setValue(item.cardType);
      if (item.cardType === CARD_ENTITIES.DRIVER || item.cardType === CARD_ENTITIES.FLEET_USER) {
        formGroup.get(PersonalizationItemKeys.userId).setValue(item.userId);
      } else {
        formGroup.get(PersonalizationItemKeys.vehicleId).setValue(item.vehicleId);
        formGroup.get(PersonalizationItemKeys.nameOnCard).setValidators(
          Validators.compose([
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(CARD_ID_MAX_LENGTH_FOR_VEHICLES),
            Validators.pattern(/^[A-Za-z0-9 ]+$/),
          ]),
        );
      }
      formGroup.get(PersonalizationItemKeys.nameOnCard).setValue(item.nameOnCard);
      formGroup.get(PersonalizationItemKeys.name).setValue(item.name);
      formGroup.get(PersonalizationItemKeys.entity).clearValidators();
      formGroup.get(PersonalizationItemKeys.entity).updateValueAndValidity();
      formGroup.get(PersonalizationItemKeys.isValidated).setValue(true);

      if (this.usedBulkEntitySelect) {
        formGroup.get(PersonalizationItemKeys.entity).setValue({
          entityType: item.cardType,
          entity: {
            id: (item.userId || item.vehicleId) as string | number,
            name: item.name,
            description: item.name,
            score: 0,
            active: true,
          },
        } as ISelectedValue);
        formGroup.get(PersonalizationItemKeys.nameOnCard).updateValueAndValidity();
        formGroup.get(PersonalizationItemKeys.nameOnCard).markAsDirty();
        formGroup.get(PersonalizationItemKeys.entity).markAsDirty();
        formGroup.get(PersonalizationItemKeys.cardIdSuggestions).setValue([]);
        formGroup.get(PersonalizationItemKeys.isValidated).setValue(false);
        formGroup[HAD_ERRORS_KEY] = false;
      }
    });
    return personalizationItems.controls.slice(initialLength) as FormGroup[];
  }

  public getBulkValidateEntryPayload(groups: FormGroup[]): IServerPersonalizationItem[] {
    const validGroups: FormGroup[] = groups.filter(
      (group: FormGroup) => this.shouldValidateEntry(group) && !this.isDuplicateNameOnCard(group),
    );

    const assignees: IServerPersonalizationItem[] = validGroups.map(group => ({
      card_type: group.get(PersonalizationItemKeys.cardType).value,
      name_on_card: group.get(PersonalizationItemKeys.cardType).value === 'driver'
        ? group.get(PersonalizationItemKeys.nameOnCard).value
        : `ID: ${group.get(PersonalizationItemKeys.nameOnCard).value}`,
      ...(this.isFleetCardsFleetUserCardsEnabled && {
        name_on_card: [ENTITY_TYPE.DRIVER, ENTITY_TYPE.FLEET_USER].includes(group.get(PersonalizationItemKeys.cardType).value)
          ? group.get(PersonalizationItemKeys.nameOnCard).value
          : `ID: ${group.get(PersonalizationItemKeys.nameOnCard).value}`,
      }),
      user_id: group.get(PersonalizationItemKeys.userId).value,
      vehicle_id: group.get(PersonalizationItemKeys.vehicleId).value,
    }));

    return assignees;
  }

  public shouldValidateEntry(group: FormGroup): boolean {
    if (
      this.isPersonalizationItemValid(group) &&
      !group.controls[PersonalizationItemKeys.isValidated].value &&
      !this.isSameAsLastValidatedEntry(group)
    ) {
      group.controls[PersonalizationItemKeys.lastValidatedValue].setValue(group.value);
      return true;
    }
    return false;
  }

  public isDuplicateNameOnCard(group: FormGroup): boolean {
    const allCardIds: string[] = [];
    const allFormGroups: FormGroup[] = this.get(CheckoutFormGroupKeys.personalizationForm).get(
      PersonalizationFormKeys.personalizationItems,
    ).value;

    allFormGroups.map((grp: FormGroup) => {
      allCardIds.push(grp[PersonalizationItemKeys.nameOnCard]);
    });

    const cardIdsSet: Set<string> = new Set<string>();
    const isDuplicate: boolean = allCardIds.some((cardId: string) => {
      if (cardIdsSet.has(cardId) && cardId === group.get(PersonalizationItemKeys.nameOnCard).value) {
        return true;
      }
      cardIdsSet.add(cardId);
      return false;
    });

    if (isDuplicate) {
      group.get(PersonalizationItemKeys.nameOnCard).markAsDirty();
      group.get(PersonalizationItemKeys.entity).markAsDirty();
      group.get(PersonalizationItemKeys.isValidated).setValue(false);
      group.setErrors(
        {
          validationError: VALIDATE_ASSIGNEE_ERROR_MAP.get(VALIDATE_ASSIGNEE_ERROR_KEYS.DUPLICATE_NAME_ON_CARD),
        },
      );
    }

    return isDuplicate;
  }

  public isPersonalizationItemValid(group: FormGroup): boolean {
    if (
      group.get(PersonalizationItemKeys.nameOnCard).valid &&
      group.get(PersonalizationItemKeys.cardType).valid &&
      (group.get(PersonalizationItemKeys.userId).valid || group.get(PersonalizationItemKeys.vehicleId).valid)
    ) return true;
    return false;
  }

  public isSameAsLastValidatedEntry(group: FormGroup): boolean {
    const lastValidatedValue = omit(
      group.controls[PersonalizationItemKeys.lastValidatedValue].value,
      PersonalizationItemKeys.lastValidatedValue,
    );
    const currentValue = omit(
      group.value,
      PersonalizationItemKeys.lastValidatedValue,
    );
    return isEqual(lastValidatedValue, currentValue);
  }

  public clear(): void {
    this.reset();
    this.importedCSV = false;
    this.hasCreatedOrder = false;
    this.filesToUpload = [];
    this.draftOrderId = '';
    this.firstOrderDetails = {};
    this.removeControl(CheckoutFormGroupKeys.personalizationForm);
    this.get(
      CheckoutFormGroupKeys.shippingAddressForm,
    ).get(ShippingAddressFormKeys.expediteShipping).setValue(SHIPPING_TYPES.STANDARD);
  }

  private isExpediteShippingSelected(): boolean {
    if (this.get(
      CheckoutFormGroupKeys.shippingAddressForm,
    ).get(ShippingAddressFormKeys.expediteShipping).value === SHIPPING_TYPES.EXPRESS) return true;
    return false;
  }

  public isPersonalizationSelected(): boolean {
    const value: string | boolean = this.get(
      CheckoutFormGroupKeys.initialRequirementsForm,
    ).get(InitialRequirementsFormKeys.personalization).value;
    return value === 'true' || value === true;
  }
  
  private initializeCardOrderFeatureFlags(): void {
    this.fleetCardsDataService.getCachedCompanyMetadata().subscribe((company: IFleetCardCompany) => {
        // Adding isFleetCardsCardOrderPublicAppEnabled to control public app card orders roll back
        this.isFleetCardsCardOrderImprovementsEnabled = 
          this.fleetCardFeatureFlagService.isFleetCardsCardOrderImprovementsEnabled() ||
          (this.fleetCardFeatureFlagService.isFleetCardsCardOrderPublicAppEnabled() && company.cardApplication.publicApp);
    
        if (this.isFleetCardsCardOrderImprovementsEnabled) {
          this.get(
            CheckoutFormGroupKeys.initialRequirementsForm,
          )['controls'][InitialRequirementsFormKeys.quantity].setValidators([
            Validators.required,
            Validators.pattern('^[0-9]*$'),
            Validators.min(1),
            Validators.max(2 * this.session.getCompositeFleetSize()), // 2 * CFS
          ]);
        }
      }, (err) => console.error(err),
    );
  }

  private isNewShippingAddressSelected(): boolean {
    return this.get(CheckoutFormGroupKeys.shippingAddressForm).get(ShippingAddressFormKeys.id).value === 'new';
  }

  private buildShippingAddress(): IServerShippingAddress | { id: string } {
    const shippingForm = this.get(CheckoutFormGroupKeys.shippingAddressForm);
    const selectedAddressDetails = history.state?.selectedAddress?.addressDetails;
    const includeAddressProofs = history.state?.addressProofs;

    // For new address only
    if (this.isNewShippingAddressSelected()) {
      return {
        address_line1: shippingForm.get(ShippingAddressFormKeys.address1).value,
        address_line2: shippingForm.get(ShippingAddressFormKeys.address2).value,
        city: shippingForm.get(ShippingAddressFormKeys.city).value,
        country: DEFAULT_COUNTRY,
        state: shippingForm.get(ShippingAddressFormKeys.state).value,
        postal_code: shippingForm.get(ShippingAddressFormKeys.postal_code).value,
        address_proofs: shippingForm.get(ShippingAddressFormKeys.proofs)?.value || this.filesToUpload?.map((f) => f.id) || [],
      };
    }
    // For address that is part of order but requires address documentation i.e not verified
    if (includeAddressProofs && selectedAddressDetails) {
      return {
        address_line1: selectedAddressDetails.addressLine1,
        address_line2: selectedAddressDetails.addressLine2,
        city: selectedAddressDetails.city,
        country: DEFAULT_COUNTRY,
        state: selectedAddressDetails.state,
        postal_code: selectedAddressDetails.postalCode,
        address_proofs: shippingForm.get(ShippingAddressFormKeys.proofs)?.value || this.filesToUpload?.map((f) => f.id) || [],
        ...(selectedAddressDetails?.id ? { id: selectedAddressDetails.id } : {}),
      };
    }
    // For verified and business address
    return {
      id: shippingForm.get(ShippingAddressFormKeys.id).value,
    };
  }

  public makeOrdersPayload(): IServerCardOrder {
    const payload: IServerCardOrder = {
      card_product_type: this.get(CheckoutFormGroupKeys.initialRequirementsForm).get(InitialRequirementsFormKeys.cardType).value,
      reason: this.get(CheckoutFormGroupKeys.initialRequirementsForm).get(InitialRequirementsFormKeys.reason).value,
      expedite_shipping: this.isExpediteShippingSelected(),
      number_of_cards: this.getNumberOfCards(),
      contact_person_details: {
        name: this.get(CheckoutFormGroupKeys.shippingAddressForm).get(ShippingAddressFormKeys.recipientName).value,
        mobile: this.get(CheckoutFormGroupKeys.shippingAddressForm).get(ShippingAddressFormKeys.recipientPhoneNumber).value,
      },
      personalization: this.get(
        CheckoutFormGroupKeys.initialRequirementsForm,
      ).get(InitialRequirementsFormKeys.personalization).value,
      shipping_address: this.buildShippingAddress(),
      ...(this.isPersonalizationSelected() && {
        personalization_items: {
          card_details: this.mapPersonalizationItems(),
          ...(this.importedCSV && {
            company_card_document_id:
              this.get(CheckoutFormGroupKeys.personalizationForm).get(PersonalizationFormKeys.documentId).value,
          }),
        },
      }),
    };

    return payload;
  }

  public mapPersonalizationItems(): IServerPersonalizationItem[] {
    return (this.get(CheckoutFormGroupKeys.personalizationForm).get(
      PersonalizationFormKeys.personalizationItems,
    ).value).map((item: FormGroup): IServerPersonalizationItem => {
      return {
        card_type: item[PersonalizationItemKeys.cardType],
        name_on_card: item[PersonalizationItemKeys.cardType] === 'vehicle' && !this.importedCSV ?
          `ID: ${item[PersonalizationItemKeys.nameOnCard]}` : item[PersonalizationItemKeys.nameOnCard],
        user_id: item[PersonalizationItemKeys.userId],
        vehicle_id: item[PersonalizationItemKeys.vehicleId],
      };
    });
  }

  private getNumberOfCards(): number {
    if (this.isPersonalizationSelected()) {
      return (this.get(CheckoutFormGroupKeys.personalizationForm).get(
        PersonalizationFormKeys.personalizationItems,
      ).value).length;
    } else {
      return this.get(CheckoutFormGroupKeys.initialRequirementsForm).get(InitialRequirementsFormKeys.quantity).value;
    }
  }
}
